/**
 * Hook for the user-initiated boost flow.
 *
 * Sequence: recommend (cache hit OR live Claude run) -> review -> pick
 * an execution path (Meta boost / dark post / manual). Cache hit comes
 * from the daily BoostPreRecommendationTask via /boost-recommendation/latest.
 */

import { useCallback, useState } from "react";

import { adsService } from "../services/adsService";
import type {
  BoostRecommendation,
  BoostRunResponse,
  CreateCampaignsResponse,
} from "../types";

interface BoostState {
  status: "idle" | "recommending" | "ready" | "creating" | "created" | "error";
  run: BoostRunResponse | null;
  creation: CreateCampaignsResponse | null;
  error: string | null;
  /** Where the current `run` came from. "cache" = pre-computed task,
   *  "live" = freshly invoked agent. Used by the UI to expose a
   *  "Re-run agent" affordance only when looking at cached output. */
  source: "cache" | "live" | null;
}

const INITIAL: BoostState = {
  status: "idle",
  run: null,
  creation: null,
  error: null,
  source: null,
};

export function useBoostRecommendation(postId: string | null) {
  const [state, setState] = useState<BoostState>(INITIAL);

  const recommend = useCallback(
    async (options?: { force?: boolean }) => {
      if (!postId) return;
      setState({ ...INITIAL, status: "recommending" });
      try {
        if (!options?.force) {
          const cached = await adsService.getLatestBoostRecommendation(postId);
          const cachedRun = cached?.task?.outputData ?? null;
          // Only honor cache when the task carries an actual agent output.
          // Failed / pending rows hand back to the live path.
          if (cachedRun && (cached?.task?.status ?? "") !== "FAILED") {
            setState({
              status: "ready",
              run: cachedRun,
              creation: null,
              error: null,
              source: "cache",
            });
            return;
          }
        }
        const run = await adsService.recommendBoost(postId);
        setState({
          status: "ready",
          run,
          creation: null,
          error: null,
          source: "live",
        });
      } catch (err) {
        setState({
          ...INITIAL,
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        });
      }
    },
    [postId],
  );

  /** Force a fresh agent run regardless of cached output. */
  const rerun = useCallback(() => recommend({ force: true }), [recommend]);

  const approve = useCallback(
    async (recommendation: BoostRecommendation) => {
      if (!postId) return;
      setState((prev) => ({ ...prev, status: "creating", error: null }));
      try {
        const creation = await adsService.createCampaigns(postId, recommendation);
        setState((prev) => ({
          ...prev,
          status: creation.anyCreated ? "created" : "error",
          creation,
          error: creation.anyCreated
            ? null
            : creation.results.map((r) => `${r.platform}: ${r.error ?? r.status}`).join("; "),
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        }));
      }
    },
    [postId],
  );

  const reset = useCallback(() => setState(INITIAL), []);

  return { ...state, recommend, rerun, approve, reset };
}
